// Create-meeting form. Renders inside Modal. Schedules a Google Meet via the
// project's connected Google account (see ProjectsSection → MeetConnectPanel).
//
// Notes on dates: <input type="datetime-local"> returns a naive string like
// "2026-05-12T15:30". We treat that as the user's local clock and convert to
// ISO via new Date(naive).toISOString() before sending — that matches what a
// human typing in the form would expect.

import React, { useEffect, useMemo, useState } from "react";
import { Video, Loader2, AlertCircle, X } from "lucide-react";
import toast from "react-hot-toast";
import Modal from "./Modal";
import { Button, Input, Textarea } from "../atoms";
import { useCreateLeadMeeting } from "../hooks/useLeadMeetings";

function pad(n) { return String(n).padStart(2, "0"); }

function defaultStart() {
  // Round up to the next 15 minutes, then add 15 more so users have a buffer.
  const d = new Date();
  d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 15));
  d.setSeconds(0, 0);
  return d;
}

function toLocalInput(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default function CreateMeetingModal({ open, onClose, leadId, lead }) {
  const create = useCreateLeadMeeting(leadId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [start, setStart] = useState(toLocalInput(defaultStart()));
  const [durationMin, setDurationMin] = useState(30);
  const [attendees, setAttendees] = useState("");
  // Recurrence: freq "none" = one-off (default). endMode "count" | "until".
  const [freq, setFreq] = useState("none");
  const [endMode, setEndMode] = useState("count");
  const [count, setCount] = useState(8);
  const [until, setUntil] = useState("");
  const [err, setErr] = useState(null);
  // Tracks whether the user has edited any field since the modal opened, so a
  // stray backdrop/Escape dismiss doesn't silently discard typed-in work.
  const [dirty, setDirty] = useState(false);

  const tz = useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; }
    catch { return "UTC"; }
  }, []);

  // Weekday name of the chosen start, for the "Weekly on Tuesday" label.
  const weekdayLabel = useMemo(() => {
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { weekday: "long" });
  }, [start]);

  useEffect(() => {
    if (!open) return;
    setTitle(lead?.title ? `${lead.title} — intro call` : "Meeting");
    setDescription("");
    setStart(toLocalInput(defaultStart()));
    setDurationMin(30);
    setAttendees(lead?.email || "");
    setFreq("none");
    setEndMode("count");
    setCount(8);
    setUntil("");
    setErr(null);
    setDirty(false);
  }, [open, lead?.title, lead?.email]);

  // Guarded close for backdrop-click / Escape: if the user has typed anything,
  // confirm before throwing it away. The X and Cancel buttons bypass this and
  // call onClose directly — an explicit close is already an explicit intent.
  const handleDismiss = () => {
    if (dirty && !window.confirm("Discard this meeting? Your changes will be lost.")) {
      return;
    }
    onClose?.();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!title.trim()) { setErr("Title is required"); return; }
    if (!start) { setErr("Start time is required"); return; }
    const startDate = new Date(start);
    if (Number.isNaN(startDate.getTime())) { setErr("Invalid start time"); return; }
    const endDate = new Date(startDate.getTime() + Number(durationMin || 30) * 60_000);

    const emails = attendees
      .split(/[,\s;]+/)
      .map((s) => s.trim())
      .filter((s) => s && /\S+@\S+\.\S+/.test(s));

    // Structured recurrence — backend builds the RRULE + generates occurrences.
    // Omitted entirely when freq === "none" (a one-off meeting).
    let recurrence;
    if (freq !== "none") {
      recurrence = { freq };
      if (endMode === "until") {
        if (!until) { setErr("Pick an end date for the recurring meeting"); return; }
        recurrence.until = new Date(until).toISOString();
      } else {
        const n = Number(count);
        if (!Number.isFinite(n) || n < 1) { setErr("Number of meetings must be at least 1"); return; }
        recurrence.count = n;
      }
    }

    try {
      await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || undefined,
        scheduledStart: startDate.toISOString(),
        scheduledEnd: endDate.toISOString(),
        timeZone: tz,
        attendees: emails,
        ...(recurrence ? { recurrence } : {}),
      });
      toast.success(recurrence ? "Recurring meeting scheduled" : "Meeting scheduled");
      onClose?.();
    } catch (e2) {
      setErr(e2?.response?.data?.error || e2.message || "Failed to schedule");
    }
  };

  // Any field edit flips dirty. Wrap a setter so onChange stays a one-liner.
  const edit = (setter) => (v) => { setDirty(true); setter(v); };

  return (
    <Modal open={open} onClose={onClose} onDismiss={handleDismiss} width={520}>
      <header style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "14px 18px", borderBottom: "1px solid var(--color-border-subtle)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Video size={15} style={{ color: "var(--crm-primary)" }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Schedule Google Meet</h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ border: "none", background: "transparent", cursor: "pointer", color: "var(--color-text-secondary)", padding: 4 }}
        >
          <X size={16} />
        </button>
      </header>

      <form onSubmit={submit} style={{ padding: 18, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>
        <Field label="Title">
          <Input value={title} onChange={(e) => edit(setTitle)(e.target.value)} placeholder="Meeting title" autoFocus />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 10 }}>
          <Field label="Start">
            <Input type="datetime-local" value={start} onChange={(e) => edit(setStart)(e.target.value)} />
          </Field>
          <Field label="Duration (min)">
            <Input
              type="number"
              min={5}
              max={480}
              step={5}
              value={durationMin}
              onChange={(e) => edit(setDurationMin)(Number(e.target.value) || 30)}
            />
          </Field>
        </div>

        <Field label="Repeat" hint="Each occurrence is its own Meet link — join + record each one separately.">
          <select
            value={freq}
            onChange={(e) => edit(setFreq)(e.target.value)}
            style={selectStyle}
          >
            <option value="none">Does not repeat</option>
            <option value="daily">Daily</option>
            <option value="weekly">{`Weekly${weekdayLabel ? ` on ${weekdayLabel}` : ""}`}</option>
            <option value="weekdays">Every weekday (Mon–Fri)</option>
          </select>
        </Field>

        {freq !== "none" && (
          <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: 10 }}>
            <Field label="Ends">
              <select value={endMode} onChange={(e) => edit(setEndMode)(e.target.value)} style={selectStyle}>
                <option value="count">After…</option>
                <option value="until">On date</option>
              </select>
            </Field>
            {endMode === "count" ? (
              <Field label="Number of meetings">
                <Input
                  type="number"
                  min={1}
                  max={52}
                  step={1}
                  value={count}
                  onChange={(e) => edit(setCount)(Number(e.target.value) || 1)}
                />
              </Field>
            ) : (
              <Field label="End date">
                <Input type="date" value={until} onChange={(e) => edit(setUntil)(e.target.value)} />
              </Field>
            )}
          </div>
        )}

        <Field label={`Timezone (${tz})`} hint="Detected from your browser; sent with the invite.">
          <Input value={tz} disabled />
        </Field>

        <Field label="Attendees" hint="Comma-separated emails. Optional.">
          <Input
            value={attendees}
            onChange={(e) => edit(setAttendees)(e.target.value)}
            placeholder="person@example.com, other@example.com"
          />
        </Field>

        <Field label="Description" hint="Optional.">
          <Textarea
            value={description}
            onChange={(e) => edit(setDescription)(e.target.value)}
            placeholder="Agenda, context, links…"
            rows={3}
            style={{ minHeight: 72 }}
          />
        </Field>

        {err && (
          <div style={{ fontSize: 12, color: "var(--crm-danger)", display: "flex", alignItems: "center", gap: 6 }}>
            <AlertCircle size={12} /> {err}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 }}>
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            disabled={create.isPending}
            leftIcon={create.isPending ? <Loader2 size={13} className="animate-spin" /> : <Video size={13} />}
          >
            {create.isPending ? "Scheduling…" : "Schedule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Match the look of the atoms/Input so the native <select> doesn't stick out.
const selectStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-border-subtle)",
  background: "var(--color-bg-surface)",
  color: "var(--color-text-primary)",
  fontSize: 13,
  cursor: "pointer",
};

function Field({ label, hint, children }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)" }}>
        {label}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{hint}</span>}
    </label>
  );
}
